<script setup>
import TenantApikomContentActionsCell from "@frontJs/components/tables/cells/projectManagement/seo/TenantApikomContentActionsCell.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { standard, premium } = defineProps({
  standard: {
    type: Boolean,
    required: false,
  },
  premium: {
    type: Boolean,
    required: false,
  },
});

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href: route("front.projectManagement.seo.apikomContents", {
            tenant: props.row.original.id,
          }),
          inertia: true,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "total",
      header: () => t("label.contents"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.total,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "totalStandard",
      header: () => t("label.standard"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.totalStandard,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "totalPremium",
      header: () => t("label.premium"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.totalPremium,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "totalToProcess",
      header: () => t("label.toProcess"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.totalToProcess,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "totalPended",
      header: () => t("label.pended"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.totalPended,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "maxDate",
      header: () => t("label.maxDateCurrent"),
      cell: (props) =>
        props.row.original.maxDate
          ? h(TextCellV2, {
              text: formatDate(
                props.row.original.maxDate,
                false,
                false,
                false,
                true,
                true,
              ),
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "maxProcessed",
      header: () => t("label.maxDateCompleted"),
      cell: (props) =>
        props.row.original.maxProcessed
          ? h(TextCellV2, {
              text: formatDate(
                props.row.original.maxProcessed,
                false,
                false,
                false,
                true,
                true,
              ),
            })
          : h(CrossIconCell),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(TenantApikomContentActionsCell, {
          row: innerProps.row,
        }),
    }),
  ],
  url: route("front.projectManagement.seo.postApikomContents"),
  data: {
    standard,
    premium,
  },
  columnVisibility: {
    totalStandard: !(standard || premium),
    totalPremium: !(standard || premium),
  },
  columnPinning: {
    right: ["actions"],
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
  />
</template>
